import "tldraw/tldraw.css";

import * as RadixContextMenu from "@radix-ui/react-context-menu";
import {
  CursorArrowIcon,
  MoveIcon,
  QuestionMarkIcon,
  MagicWandIcon,
  Pencil2Icon,
  EraserIcon,
  ArrowRightIcon,
  CursorTextIcon,
  ResetIcon,
  FilePlusIcon,
  StarFilledIcon,
} from "@radix-ui/react-icons";
import {
  QueryClientProvider,
  useQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { capitalCase } from "change-case";
import {
  BeakerIcon,
  FilterIcon,
  LayersIcon,
  ListCheck,
  ListPlus,
  PencilIcon,
  SquarePlusIcon,
  StickyNoteIcon,
} from "lucide-react";
import React, {
  ComponentProps,
  createContext,
  lazy,
  memo,
  Suspense,
  use,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { mergeProps, useLongPress, usePress } from "react-aria";
import { Autocomplete, ListBox, ListBoxItem } from "react-aria-components";
import Joyride, { Step } from "react-joyride";
import { useAsyncList } from "react-stately";
import {
  ArrangeMenuSubmenu,
  BindingOnCreateOptions,
  Box,
  CopyMenuItem,
  DefaultCanvas,
  DefaultContextMenu,
  DeleteMenuItem,
  DuplicateMenuItem,
  Editor,
  PasteMenuItem,
  ReorderMenuSubmenu,
  SelectAllMenuItem,
  TLBinding,
  TLComponents,
  TLShapeId,
  TLUiContextMenuProps,
  TLUiOverrides,
  TLUiToolItem,
  TLUiToolsContextType,
  Tldraw,
  TldrawUiContextProvider,
  TldrawUiMenuGroup,
  TldrawUiMenuItem,
  TldrawUiMenuSubmenu,
  createBindingId,
  setUserPreferences,
  toRichText,
  track,
  useEditor,
  useIsToolSelected,
  useTools,
  useValue,
} from "tldraw";
import { useDarkMode } from "usehooks-ts";

import { AddPostPanel } from "@/components/AddPostPanel";
import { clearAuditLog } from "@/components/canvas-editor/annotate-feed";
import {
  allBoundChildren,
  isLayoutBinding,
  isPageRegionChild,
  LayoutBindingUtil,
} from "@/components/canvas-editor/bindings/layout";
import {
  buildInitialEditor,
  buildManifestFromState,
  rebuildEditorFromManifest,
} from "@/components/canvas-editor/build-manifest";
import {
  canvasEditorMachine,
  CanvasEditorMachine,
  Panel,
} from "@/components/canvas-editor/canvas-editor-machine";
import {
  ColorContext,
  colorVariants,
  theme,
} from "@/components/canvas-editor/components/Container";
import { NodeCursor } from "@/components/canvas-editor/components/NodeCursor";
import { GrazeFeedIdContext } from "@/components/canvas-editor/GrazeFeedIdContext";
import { InFrontOfCanvasPortalContext } from "@/components/canvas-editor/InFrontOfCanvasPortal";
import {
  pageRefitHeld,
  reconcileLayerShapes,
  renormaliseShares,
} from "@/components/canvas-editor/layers";
import { Minimap } from "@/components/canvas-editor/Minimap";
import {
  NodeDocs,
  NodeDocsDescription,
  NodeDocsHeader,
  NodeDocsSubHeader,
} from "@/components/canvas-editor/NodeDocs";
import {
  AttributeCompareShapeUtil,
  AttributeCompareTool,
} from "@/components/canvas-editor/shapes/attribute-compare";
import {
  AudienceBoostShapeUtil,
  AudienceBoostTool,
} from "@/components/canvas-editor/shapes/audience-boost";
import {
  AudienceFilterShapeUtil,
  AudienceFilterTool,
} from "@/components/canvas-editor/shapes/audience-filter";
import {
  AudienceLogicShapeUtil,
  AudienceLogicTool,
} from "@/components/canvas-editor/shapes/audience-logic";
import {
  BindableShape,
  BindableUtil,
  isBindableShape,
  isReadOnlyEditorPage,
} from "@/components/canvas-editor/shapes/bindable";
import {
  ContentModerationShapeUtil,
  ContentModerationTool,
} from "@/components/canvas-editor/shapes/content-moderation";
import {
  CustomNode,
  CustomNodeShapeUtil,
  CustomNodeTool,
} from "@/components/canvas-editor/shapes/custom-node";
import { CustomNodeRootShapeUtil } from "@/components/canvas-editor/shapes/custom-node-definition";
import {
  EachShapeUtil,
  EachTool,
} from "@/components/canvas-editor/shapes/each";
import {
  EmbedShapeUtil,
  EmbedTool,
} from "@/components/canvas-editor/shapes/embed";
import {
  EmotionAnalysisShapeUtil,
  EmotionAnalysisTool,
} from "@/components/canvas-editor/shapes/emotion-analysis";
import {
  EntityListShapeUtil,
  EntityListTool,
} from "@/components/canvas-editor/shapes/entity-list";
import { FeedShapeUtil, getFeedShape } from "@/components/canvas-editor/shapes/feed";
import {
  FinancialSentimentAnalysisShapeUtil,
  FinancialSentimentAnalysisTool,
} from "@/components/canvas-editor/shapes/financial-sentiment-analysis";
import {
  ImageCategorizationShapeUtil,
  ImageCategorizationTool,
} from "@/components/canvas-editor/shapes/image-categorization";
import {
  ImageModerationShapeUtil,
  ImageModerationTool,
} from "@/components/canvas-editor/shapes/image-moderation";
import {
  LabelerLabelShapeUtil,
  LabelerLabelTool,
} from "@/components/canvas-editor/shapes/labeler-label";
import {
  LanguageAnalysisShapeUtil,
  LanguageAnalysisTool,
} from "@/components/canvas-editor/shapes/language-analysis";
import {
  GymLayerTool,
  LayerShapeUtil,
  orderingLayerTools,
  orderingLayerToolIds,
} from "@/components/canvas-editor/shapes/layer";
import {
  ListMemberShapeUtil,
  ListMemberTool,
} from "@/components/canvas-editor/shapes/list-member";
import {
  LogicShapeUtil,
  LogicTool,
} from "@/components/canvas-editor/shapes/logic";
import {
  MagicAudienceShapeUtil,
  MagicAudienceTool,
} from "@/components/canvas-editor/shapes/magic-audience";
import {
  ModelProbabilityShapeUtil,
  ModelProbabilityTool,
} from "@/components/canvas-editor/shapes/model-probability";
import {
  MultiRegexTool,
  MultiRegexShapeUtil,
} from "@/components/canvas-editor/shapes/multi-regex";
import { NotesUtil, NotesTool } from "@/components/canvas-editor/shapes/notes";
import {
  OpenAIShapeUtil,
  OpenAITool,
} from "@/components/canvas-editor/shapes/openai";
import {
  ParamCompareShapeUtil,
  ParamCompareTool,
} from "@/components/canvas-editor/shapes/param-compare";
import {
  PostTypeShapeUtil,
  PostTypeTool,
} from "@/components/canvas-editor/shapes/post-type";
import {
  RatioCompareShapeUtil,
  RatioCompareTool,
} from "@/components/canvas-editor/shapes/ratio-compare";
import {
  SentimentAnalysisShapeUtil,
  SentimentAnalysisTool,
} from "@/components/canvas-editor/shapes/sentiment-analysis";
import {
  CustomNodeRootShape,
  FeedShape,
  LayoutBinding,
  LogicShape,
  customNodeType,
  feedType,
  isFeedShape,
  isLayerShape,
  magicAudienceType,
  patterns,
} from "@/components/canvas-editor/shapes/shapes";
import {
  SimpleRegexShapeUtil,
  SimpleRegexTool,
} from "@/components/canvas-editor/shapes/simple-regex";
import {
  SocialGraphShapeUtil,
  SocialGraphTool,
} from "@/components/canvas-editor/shapes/social-graph";
import {
  SocialListShapeUtil,
  SocialListTool,
} from "@/components/canvas-editor/shapes/social-list";
import {
  SpamAnalysisShapeUtil,
  SpamAnalysisTool,
} from "@/components/canvas-editor/shapes/spam-analysis";
import {
  StarterPackShapeUtil,
  StarterPackTool,
} from "@/components/canvas-editor/shapes/starter-pack";
import { TargetPlaceholderShapeUtil } from "@/components/canvas-editor/shapes/target-placeholder";
import {
  TextCategorizationShapeUtil,
  TextCategorizationTool,
} from "@/components/canvas-editor/shapes/text-categorization";
import {
  TextQuestionShapeUtil,
  TextQuestionTool,
} from "@/components/canvas-editor/shapes/text-question";
import {
  TextSimilarityShapeUtil,
  TextSimilarityTool,
} from "@/components/canvas-editor/shapes/text-similarity";
import {
  DateBetweenShapeUtil,
  DateBetweenTool,
  HourOfDayBetweenShapeUtil,
  HourOfDayBetweenTool,
} from "@/components/canvas-editor/shapes/time-window";
import {
  TopicAnalysisShapeUtil,
  TopicAnalysisTool,
} from "@/components/canvas-editor/shapes/topic-analysis";
import {
  ToxicityAnalysisShapeUtil,
  ToxicityAnalysisTool,
} from "@/components/canvas-editor/shapes/toxicity-analysis";
import {
  centerContent,
  compact,
  CustomNodeEditorContext,
  EditingCustomNodeIdContext,
  defaultFeedVersion,
  feedId,
  preventBubble,
  previewId,
  ReadonlyEditorContext,
} from "@/components/canvas-editor/utils";
import { CustomNodeFilterSelect } from "@/components/CustomNodeFilterSelect";
import { useIsMobile } from "@/components/hooks/use-mobile";
import { useToast } from "@/components/hooks/use-toast";
import { useEvent } from "@/components/hooks/useEvent";
import { MarkdownRenderer } from "@/components/MarkdownRenderer";
import {
  PreviewFeed,
  PreviewFeedProps,
  WorkbenchPreviewFeed,
} from "@/components/PreviewFeed";
import { Spinner } from "@/components/Spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogContent,
} from "@/components/ui/dialog";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toggle } from "@/components/ui/toggle";
import { SimpleTooltip, TooltipProvider } from "@/components/ui/tooltip";
import { WorkbenchDebugPost } from "@/components/WorkbenchDebugPost";
import {
  CustomNodeHistoryItem,
  feedQueryOptions,
  getCustomNodeQueryOptions,
  queryClient,
  searchCustomNodes,
} from "@/lib/api";
import { getSnapshot } from "@/lib/getSnapshot";
import {
  FeedParameter,
  FeedVersion,
  Manifest,
  NodeMetadata,
  Post,
  SortingParams,
} from "@/lib/types";
import {
  CustomNodeFilter,
  useDefaultCustomNodes,
} from "@/lib/useDefaultCustomNodes";
import { atUriToBskyUrl, cn } from "@/lib/utils";

export const DefaultCustomNodeIdContext = createContext<number | undefined>(
  undefined,
);

const simpleTools = [
  // General purpose/good starting point
  MultiRegexTool,
  EntityListTool,
  EmbedTool,
  PostTypeTool,
  // Advanced "simple" tools
  SimpleRegexTool,
  AttributeCompareTool,
  RatioCompareTool,
  HourOfDayBetweenTool,
  DateBetweenTool,
];
const listTools = [
  // Social Tools
  ListMemberTool,
  LabelerLabelTool,
  SocialGraphTool,
  StarterPackTool,
  SocialListTool,
];
const compositionTools = [LogicTool];
const advancedTools = [
  // Easy to use models
  ContentModerationTool,
  ImageModerationTool,
  TextCategorizationTool,
  ImageCategorizationTool,
  SpamAnalysisTool,
  TopicAnalysisTool,
  LanguageAnalysisTool,
  SentimentAnalysisTool,
  ToxicityAnalysisTool,
  EmotionAnalysisTool,
  FinancialSentimentAnalysisTool,
  TextSimilarityTool,
  MagicAudienceTool,
  ModelProbabilityTool,
];
const markupTools = [NotesTool];
/**
 * Page layers and audience blocks. Not conditions: they never enter the match
 * tree, so they are absent from the custom-node editor, where there is no page
 * to divide. The audience blocks are generally available; the Gym Layer follows
 * the `feed_gym` flag.
 */
interface ToolOptions {
  showOpenAITool: boolean;
  /** "Ask a Question" — beta-gated, like the Natural Language node, and for the same reason:
   * placing it spends a model call per post. */
  showTextQuestionTool: boolean;
  showLayerTools: boolean;
  showAudienceTools: boolean;
  /** Any/all groups over filters — the v3 lens contract. Lit only once the serving fleet reads v3. */
  showGroupTool: boolean;
  showGymTool: boolean;
}
const getLayerTools = ({
  showLayerTools,
  showAudienceTools,
  showGroupTool,
  showGymTool,
}: Pick<ToolOptions, "showLayerTools" | "showAudienceTools" | "showGroupTool" | "showGymTool">) =>
  showLayerTools
    ? [
        ...orderingLayerTools,
        ...(showGymTool ? [GymLayerTool] : []),
        ...(showAudienceTools
          ? [
              AudienceFilterTool,
              ...(showGroupTool ? [AudienceLogicTool] : []),
              AudienceBoostTool,
            ]
          : []),
      ]
    : [];
const getCustomTools = (options: ToolOptions) => [
  ...(options.showOpenAITool ? [OpenAITool] : []),
  ...(options.showTextQuestionTool ? [TextQuestionTool] : []),
  ...markupTools,
  ...simpleTools,
  ...listTools,
  ...compositionTools,
  ...getLayerTools(options),
  ...advancedTools,
  CustomNodeTool,
];
type Tool = ReturnType<typeof getCustomTools>[number];
const shapes = [
  SimpleRegexShapeUtil,
  MultiRegexShapeUtil,
  PostTypeShapeUtil,
  LogicShapeUtil,
  AttributeCompareShapeUtil,
  RatioCompareShapeUtil,
  TextQuestionShapeUtil,
  HourOfDayBetweenShapeUtil,
  DateBetweenShapeUtil,
  EmbedShapeUtil,
  TextSimilarityShapeUtil,
  SocialGraphShapeUtil,
  StarterPackShapeUtil,
  MagicAudienceShapeUtil,
  ListMemberShapeUtil,
  LabelerLabelShapeUtil,
  SocialListShapeUtil,
  ContentModerationShapeUtil,
  FeedShapeUtil,
  CustomNodeRootShapeUtil,
  ModelProbabilityShapeUtil,
  EntityListShapeUtil,
  TargetPlaceholderShapeUtil,
  LanguageAnalysisShapeUtil,
  SentimentAnalysisShapeUtil,
  FinancialSentimentAnalysisShapeUtil,
  ToxicityAnalysisShapeUtil,
  EmotionAnalysisShapeUtil,
  SpamAnalysisShapeUtil,
  TopicAnalysisShapeUtil,
  NotesUtil,
  TextCategorizationShapeUtil,
  ImageModerationShapeUtil,
  ImageCategorizationShapeUtil,
  CustomNodeShapeUtil,
  ParamCompareShapeUtil,
  EachShapeUtil,
  OpenAIShapeUtil,
  LayerShapeUtil,
  AudienceFilterShapeUtil,
  AudienceBoostShapeUtil,
  AudienceLogicShapeUtil,
];
const bindingUtils = [LayoutBindingUtil];

const getUiOverrides = ({
  isReadOnly,
  isCustomNode,
  toolOptions,
}: {
  isReadOnly: boolean;
  isCustomNode: boolean;
  toolOptions: ToolOptions;
}): TLUiOverrides => ({
  tools(editor, tools) {
    if (isReadOnly) {
      return {
        hand: tools.hand,
        select: tools.select,
      } as TLUiToolsContextType;
    }

    const customTools = getCustomTools(toolOptions);

    for (const tool of customTools) {
      tools[tool.id] = tool.uiToolDef(editor) as unknown as TLUiToolItem<
        string,
        string
      >;
    }

    if (isCustomNode) {
      tools[ParamCompareTool.id] = ParamCompareTool.uiToolDef(
        editor,
      ) as unknown as TLUiToolItem<string, string>;
      tools[EachTool.id] = EachTool.uiToolDef(
        editor,
      ) as unknown as TLUiToolItem<string, string>;
    }

    tools[NotesTool.id] = NotesTool.uiToolDef(
      editor,
    ) as unknown as TLUiToolItem<string, string>;

    return tools;
  },
});

interface GrazeMenuItemProps extends ComponentProps<"button"> {
  icon: React.ReactNode;
  label: string;
  shortcut?: string;
  /** A mark after the label — today, that this node is metered. */
  badge?: React.ReactNode;
}

export const GrazeMenuItem = function GrazeMenuItem({
  icon,
  label,
  shortcut,
  badge,
  className,
  ...props
}: GrazeMenuItemProps) {
  return (
    <button
      type="button"
      className={cn(
        "text-slate-dim",
        "text-left p-2 rounded flex gap-2 items-center",
        "bg-slatea-ghost hover:text-slate-normal",
        "group-data-[focus-visible='true']:bg-slate-action",
        className,
      )}
      {...props}
    >
      <div className="flex justify-center items-center">{icon}</div>
      {/* The badge sits WITH the label, not in the trailing column. That column is where keyboard
          shortcuts live, so a bare icon there reads as a broken shortcut -- reported from the
          palette, where the meter icon sat directly above Content Moderation's "C" and looked
          like an artifact of it. */}
      <span className={cn("text-xs flex-1 flex items-center gap-1.5")}>
        {label}
        {badge}
      </span>
      {shortcut && (
        <span className={cn("text-xs uppercase font-mono pl-4")}>
          {shortcut}
        </span>
      )}
    </button>
  );
};

const JsonEditor = lazy(() => import("@/components/JsonEditor"));

function LazyJsonEditor({
  feedVersion,
  onChange,
}: {
  feedVersion: FeedVersion;
  onChange?: (feedVersion: FeedVersion) => void;
}) {
  const audit = CanvasEditorMachine.useSelector((s) =>
    s.context.debugPostUrl
      ? s.context.debugResults[s.context.debugPostUrl]
      : undefined,
  );

  return (
    <Suspense
      fallback={
        <div className="h-full w-full flex items-center justify-center">
          <Spinner delay={500} />
        </div>
      }
    >
      <JsonEditor
        feedVersion={feedVersion}
        onChange={
          onChange ? (newFeedVersion) => onChange(newFeedVersion) : undefined
        }
        audit={audit}
      />
    </Suspense>
  );
}

function LongPressDialog({
  trigger,
  content,
  onPress,
}: {
  trigger: React.ReactNode;
  content: React.ReactNode;
  onPress: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { pressProps } = usePress({ onPress });
  const { longPressProps } = useLongPress({
    accessibilityDescription: "Long press for help",
    onLongPress: () => setOpen(true),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger
        asChild
        {...mergeProps(longPressProps, pressProps, {
          onClick: (e: Event) => e.preventDefault(),
        })}
      >
        {trigger}
      </DialogTrigger>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Node Help</DialogTitle>
        </DialogHeader>
        <div>{content}</div>
      </DialogContent>
    </Dialog>
  );
}

function ToolInfo({
  tool,
  isFocusVisible,
  className,
  onClick: onClickProp,
}: {
  tool: Tool;
  isFocusVisible?: boolean;
  className?: string;
  onClick?: () => void;
}) {
  const editor = useEditor();
  const def = tool.uiToolDef(editor);
  const [open, setOpen] = useState(Boolean(isFocusVisible));
  const [boundary, setBoundary] = useState<HTMLElement | null>(null);
  const closeToolbar = use(CloseToolbarContext);
  const isMobile = useIsMobile();

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    setOpen(Boolean(isFocusVisible));
  }, [isFocusVisible]);

  useEffect(() => {
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
    setBoundary(editor.getContainer());
  }, [editor]);

  const onClick = useEvent(() => {
    editor.setCurrentTool("select");
    requestAnimationFrame(() =>
      (onClickProp || editor.setCurrentTool.bind(editor))(def.id),
    );

    if (!isMobile) {
      setOpen(false);
    } else {
      editor.menus.clearOpenMenus();
    }

    closeToolbar();
  });

  const item = (
    <GrazeMenuItem
      type="button"
      className={className}
      data-graze-tool={true}
      icon={
        typeof def.icon === "string" ? (
          def.icon
        ) : (
          <def.icon className="h-4 w-4 pointer-events-none" />
        )
      }
      shortcut={"kbd" in def ? def.kbd : undefined}
      label={def.label}
      badge={
        "rateLimited" in def && def.rateLimited ? (
          // A word, not a lone icon. "What is this gauge?" is a worse question to leave a reader
          // with than a label that says the thing outright, and it costs four characters.
          <span
            className="shrink-0 rounded-full bg-ambera-action px-1.5 py-px text-[10px] leading-4 font-medium text-amber-normal"
            title="This node has an hourly budget: scoring runs on a GPU."
          >
            metered
          </span>
        ) : undefined
      }
      onPointerDown={(e) => e.preventDefault()}
    />
  );

  if (tool.id === ModelProbabilityShapeUtil.type) {
    return null;
  }

  if (isMobile) {
    return (
      <LongPressDialog
        trigger={item}
        onPress={onClick}
        content={<def.docs />}
      />
    );
  }

  return (
    <HoverCard key={def.id} open={open} onOpenChange={setOpen} openDelay={1000}>
      <HoverCardTrigger asChild onClick={onClick}>
        {item}
      </HoverCardTrigger>
      <HoverCardContent
        className="w-min"
        side="right"
        collisionBoundary={boundary}
      >
        <def.docs />
      </HoverCardContent>
    </HoverCard>
  );
}

const description =
  "The Graze editor is a powerful tool for designing feeds. The editor consists of a number of nodes. Each node filters posts to create a feed.";

function HelpMenu({
  trigger,
  startTour,
  tools,
}: {
  trigger: React.ReactNode;
  startTour: () => void;
  tools: Tool[];
}) {
  const editor = useEditor();
  const isMobile = useIsMobile();
  const docs = (
    <>
      <NodeDocsSubHeader>Feed Node</NodeDocsSubHeader>

      <NodeDocsDescription>
        Every feed starts with the &ldquo;Feed&rdquo; node. This node defines
        your feed logic and can&apos;t be deleted.
      </NodeDocsDescription>

      <NodeDocsDescription>
        The &ldquo;Feed&rdquo; node can only have one child. This child can be
        any one of the other node types. This will be the only filter applied to
        your feed. To combine nodes you can use the &ldquo;Logic&rdquo; node.
      </NodeDocsDescription>

      <NodeDocsSubHeader>Adding Nodes</NodeDocsSubHeader>

      <NodeDocsDescription>
        To add a node, first select a node from the sidebar. This will set that
        node as active. Then click the canvas to add a new node.
      </NodeDocsDescription>

      <NodeDocsDescription>
        Once you&apos;ve added a node, you can drag it into either a
        &ldquo;Feed&rdquo; or a &ldquo;Logic&rdquo; node.
      </NodeDocsDescription>
    </>
  );

  if (isMobile) {
    return (
      <Dialog>
        <DialogTrigger asChild>{trigger}</DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Help</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
            <div className="flex flex-col gap-4">
              {docs}

              <Separator />

              <div className="flex flex-col gap-8 text-left">
                {tools.map((t) => {
                  if (t.id === "magic-audience") {
                    return null;
                  }

                  const def = t.uiToolDef(editor);
                  return (
                    <div key={t.id}>
                      <def.docs />
                    </div>
                  );
                })}
              </div>
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>{trigger}</HoverCardTrigger>
      <HoverCardContent className="w-auto max-w-md" side="right">
        <NodeDocsHeader>
          <span className="flex-1">Help</span>
          <Button size="sm" variant="outline" onPress={startTour}>
            Tour Editor
          </Button>
        </NodeDocsHeader>
        <NodeDocsDescription>{description}</NodeDocsDescription>

        {docs}
      </HoverCardContent>
    </HoverCard>
  );
}

const CloseToolbarContext = createContext(() => {});

/*
 * If your steps are not dynamic you can use a simple array.
 * Otherwise you can set it as a state inside your component.
 */
const introSteps: Array<Step> = [
  {
    target: ".tl-canvas",
    disableBeacon: true,
    title: "Working in Graze",
    content:
      "This is the Graze canvas. It is where you build the logic for your feed.",
  },
  {
    target: "#node-trigger",
    title: "Node Filters",
    content:
      'To add customize your feed you will add more "nodes" to filter the posts from Bluesky.',
    disableBeacon: true,
    placement: "right",
  },
  {
    target: "#text-trigger",
    title: "Annotations",
    content:
      "To help you document your work you can use text, arrows and notes nodes to annotate your feed.",
    disableBeacon: true,
    placement: "right",
  },
  {
    target: "#undo-trigger",
    title: "History",
    content:
      "The canvas has undo/redo history. Every version you pubilshed is in your versions history. So if you need to revert your changes you can!",
    disableBeacon: true,
    placement: "right",
  },
  {
    target: "#generate-feed-trigger",
    title: "Generate from prompt",
    content:
      "If you have trouble getting started you can use AI to generate the initial feed logic for you.",
    disableBeacon: true,
    placement: "right",
  },
  {
    target: "#generate-feed-tool",
    title: "Preview Feeds",
    content:
      "Graze works by using the filter you create to determine if a post from Bluesky should be included in your feed. This doesn't currently cover posts in the past. Use this tool to crawl past posts to see what might be included in your feed.",
    disableBeacon: true,
    placement: "bottom",
  },
  {
    target: "#debug-post-tool",
    title: "Debug Post",
    content:
      "You can also test your feed logic against a specific post. This is very useful to test your feed's logic as your build it.",
    disableBeacon: true,
    placement: "bottom",
  },
];

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="text-xs font-medium px-2 py-1 text-slate-10 dark:text-slatedark-10">
    {children}
  </div>
);

function CustomTool({ node }: { node: CustomNodeHistoryItem }) {
  const editor = useEditor();
  const closeToolbar = use(CloseToolbarContext);

  const toolClass = useMemo(() => {
    // eslint-disable-next-line react-hooks/unsupported-syntax
    class Foo {
      static id = node.id;
      static uiToolDef = () => ({
        label: node.title,
        docs: () => (
          <NodeDocs
            editor={editor}
            title={node.title}
            util={CustomNodeShapeUtil}
            component={CustomNode}
            description={
              <MarkdownRenderer>{node.description}</MarkdownRenderer>
            }
            extraProps={{
              filter: node.component,
              node,
              metadata: node.component.metadata,
            }}
          />
        ),
        icon: () => {
          return node.favorited ? (
            <StarFilledIcon className="w-4 h-4" />
          ) : (
            <Avatar className="w-5 h-5" src={node.avatar} alt={node.title} />
          );
        },
      });
      static isLockable = false;
    }

    return Foo;
  }, [node, editor]);

  const color = node.component.metadata?.color || node.color;

  const onAction = useEvent(() => {
    CustomNodeTool.uiToolDef(editor).onActivate({
      filter: node.component,
      node: {
        title: node.title,
        description: node.description,
        component_id: node.id,
        version_id: node.version_id,
        // A new embed tracks non-breaking updates by default, which is what
        // `npm install` writes when it records `^`. Existing embeds are
        // unaffected: absence still means `pinned`, so nothing already stored
        // changes and no compiled bytes move.
        dependency_intent: "compatible" as const,
      },
    });
    closeToolbar();
  });

  return (
    <ListBoxItem
      textValue={node.title}
      onAction={onAction}
      className="group flex"
    >
      {({ isFocusVisible }) => (
        <div
          className="flex-1 flex"
          // Prevent the input from being focused when the item is clicked
          onPointerDown={(e) => e.stopPropagation()}
        >
          <ToolInfo
            key={node.id}
            tool={toolClass as unknown as Tool}
            isFocusVisible={isFocusVisible}
            onClick={onAction}
            className={cn(
              "flex-1",
              color && theme.button[color === "slate" ? "default" : color],
            )}
          />
        </div>
      )}
    </ListBoxItem>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col gap-2 pt-4 px-4">
      <div className="text-sm text-slate-dim">No custom nodes found</div>
    </div>
  );
}

const MemoizedResults = memo(function MemoizedResults({
  listData,
  isFetchingUserCustomNodes,
  isLoading,
}: {
  listData: CustomNodeHistoryItem[];
  isFetchingUserCustomNodes: boolean;
  isLoading: boolean;
}) {
  return (
    <ListBox
      items={listData}
      className="flex flex-col py-1 gap-1"
      renderEmptyState={() => {
        if (isFetchingUserCustomNodes || isLoading) {
          return (
            <div className="flex items-center justify-center p-4 gap-2">
              <Spinner className="h-4 w-4" />
            </div>
          );
        }

        return <EmptyState />;
      }}
    >
      {(node) => <CustomTool node={node} />}
    </ListBox>
  );
});

function CustomNodeTab() {
  const defaultCustomNodeId = use(DefaultCustomNodeIdContext);
  // A node cannot be added to itself. Embedding is by value, so a self-embed
  // does not recurse -- it bakes the previous saved body in one level, and
  // doubles the stored body on every save after that. The server refuses it
  // too; this is so the option is never offered in the first place.
  const editingCustomNodeId = use(EditingCustomNodeIdContext);
  const [filter, setFilter] = useState<CustomNodeFilter>("all");
  const { data: userCustomNodes, isFetching: isFetchingUserCustomNodes } =
    useDefaultCustomNodes({ suspense: false, filter });
  const { data: defaultCustomNode } = defaultCustomNodeId
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuery(getCustomNodeQueryOptions(defaultCustomNodeId))
    : { data: undefined };
  const list = useAsyncList<CustomNodeHistoryItem>({
    async load({ filterText }) {
      return {
        items: (await searchCustomNodes({ query: filterText || "" })).results,
      };
    },
  });

  const listData = useMemo(() => {
    let listData: CustomNodeHistoryItem[] = [];

    if (list.filterText) {
      listData = list.items;
    } else if (defaultCustomNodeId) {
      listData = defaultCustomNode ? [defaultCustomNode] : [];
    } else if (userCustomNodes) {
      listData = userCustomNodes;
    }

    // Applied after every branch above, including search results -- searching
    // for a node by name is the easiest way to find yourself.
    if (editingCustomNodeId !== undefined) {
      listData = listData.filter((item) => item.id !== editingCustomNodeId);
    }

    return listData;
  }, [
    list.filterText,
    list.items,
    defaultCustomNodeId,
    userCustomNodes,
    defaultCustomNode,
    editingCustomNodeId,
  ]);

  return (
    <Autocomplete
      inputValue={list.filterText}
      onInputChange={(v) => list.setFilterText(v)}
    >
      <div className="flex items-center gap-1">
        <SearchInput
          placeholder="Search custom nodes..."
          className="w-full"
          aria-label="Search custom nodes"
          sizeVariant="sm"
        />
        {!list.filterText && (
          <CustomNodeFilterSelect filter={filter} setFilter={setFilter} />
        )}
      </div>
      <MemoizedResults
        listData={listData}
        isFetchingUserCustomNodes={isFetchingUserCustomNodes}
        isLoading={list.isLoading}
      />
    </Autocomplete>
  );
}

const placeholderTool = {
  id: "foo",
  label: "Select",
  icon: "",
  onSelect: () => {},
};

function Sidebar({
  editorActions,
  onStartTemplateMode,
  onStartAmendMode,
  customTools,
  mode,
}: {
  editorActions?: React.ReactNode;
  onStartTemplateMode?: () => void;
  onStartAmendMode?: () => void;
  customTools: Tool[];
  mode: "canvas" | "json";
}) {
  const isCustomNodeEditor = use(CustomNodeEditorContext);
  const tools = useTools();
  const editor = useEditor();
  const isReadOnly = use(ReadonlyEditorContext);

  const isSelectSelected = useIsToolSelected(
    tools["select"] || placeholderTool,
  );
  const isHandSelected = useIsToolSelected(tools["hand"] || placeholderTool);
  const isEraserSelected = useIsToolSelected(
    tools["eraser"] || placeholderTool,
  );
  const { isDarkMode } = useDarkMode();

  const [runTour, setRunTour] = useState(false);
  const [shapeMenuOpen, setShapeMenuOpen] = useState(false);
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  const onShapeMenuOpenChange = useEvent((open: boolean) => {
    setShapeMenuOpen(open);
    editor.updateInstanceState({ isReadonly: open });
  });
  const closeToolbar = useEvent(() => {
    onShapeMenuOpenChange(false);
    setPageMenuOpen(false);
    document.querySelector<HTMLElement>(".tl-container")?.focus();
  });

  const hasOpenAITool = useMemo(
    () => customTools.some((t) => t.id === OpenAITool.id),
    [customTools],
  );
  // Asked of `customTools` rather than of the flag directly, so the docs menu cannot drift
  // from the palette: whatever the gate decides, both surfaces read the same answer.
  const hasTextQuestionTool = useMemo(
    () => customTools.some((t) => t.id === TextQuestionTool.id),
    [customTools],
  );
  // The Page tab: one entry per ordering, the gym, then the audience blocks.
  // Filtered from the registered tools so the flags that gate them apply.
  const orderingTools = useMemo(
    () => customTools.filter((t) => orderingLayerToolIds.has(t.id)),
    [customTools],
  );
  const gymTools = useMemo(
    () => customTools.filter((t) => t.id === GymLayerTool.id),
    [customTools],
  );
  const audienceTools = useMemo(() => {
    const ids = new Set<string>([
      AudienceFilterTool.id,
      AudienceLogicTool.id,
      AudienceBoostTool.id,
    ]);
    return customTools.filter((t) => ids.has(t.id));
  }, [customTools]);

  return (
    <div
      className="h-full flex flex-col pointer-events-auto"
      {...preventBubble}
    >
      <Joyride
        steps={introSteps}
        run={runTour}
        continuous={true}
        showSkipButton={true}
        locale={{ last: "Finish" }}
        styles={{
          options: {
            arrowColor: isDarkMode ? "rgb(217 217 224)" : "rgb(24 25 27)",
            backgroundColor: isDarkMode ? "rgb(217 217 224)" : "rgb(24 25 27)",
            textColor: isDarkMode ? "rgb(28 32 36)" : "rgb(237 238 240)",
            primaryColor: isDarkMode ? "rgb(119 123 132)" : "rgb(128 131 141)",
          },
          buttonBack: {
            color: isDarkMode ? "rgb(96 100 108)" : "rgb(176 180 186)",
          },
        }}
      />
      <div className="flex flex-col p-2 border-r border-slate-6 dark:border-slatedark-6 bg-slate-app w-fit h-full canvas-sidebar">
        <div className="flex flex-col flex-1">
          {editorActions}
          {!isReadOnly && mode === "canvas" && (
            <>
              {editorActions && <Separator className="my-2" />}
              <div className="flex flex-col gap-1">
                <CloseToolbarContext value={closeToolbar}>
                  <Popover
                    open={shapeMenuOpen}
                    onOpenChange={onShapeMenuOpenChange}
                  >
                    <SimpleTooltip message="Rules">
                      <PopoverTrigger asChild>
                        <Button size="smIcon" variant="ghost" id="node-trigger">
                          <span className="sr-only">Rules</span>
                          <FilterIcon className="h-4 w-4" />
                        </Button>
                      </PopoverTrigger>
                    </SimpleTooltip>
                    <PopoverContent
                      side="right"
                      sideOffset={16}
                      className="flex flex-col px-2 py-2 bg-slate-app"
                      id="node-list"
                      tabIndex={0}
                      onOpenAutoFocus={(e) => {
                        e.preventDefault();
                        (e.target as HTMLElement).focus();
                      }}
                    >
                      <Tabs defaultValue="basic">
                        <TabsList>
                          <TabsTrigger value="basic" size="sm">
                            Basic
                          </TabsTrigger>
                          <TabsTrigger value="ai" size="sm">
                            AI
                          </TabsTrigger>
                          <TabsTrigger value="markup" size="sm">
                            Markup
                          </TabsTrigger>
                          <TabsTrigger value="custom" size="sm">
                            Custom
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent value="basic" className="flex flex-col">
                          {isCustomNodeEditor && (
                            <>
                              <SectionLabel>Custom</SectionLabel>
                              <ToolInfo
                                tool={ParamCompareTool}
                                key={ParamCompareTool.id}
                              />
                              <ToolInfo tool={EachTool} key={EachTool.id} />
                              <Separator className="my-2" />
                            </>
                          )}
                          <SectionLabel>Attribute</SectionLabel>
                          {simpleTools.map((tool) => (
                            <ToolInfo tool={tool} key={tool.id} />
                          ))}
                          <Separator className="my-2" />
                          <SectionLabel>List</SectionLabel>
                          {listTools.map((tool) => (
                            <ToolInfo tool={tool} key={tool.id} />
                          ))}
                          <Separator className="my-2" />
                          {compositionTools.map((tool) => (
                            <ToolInfo tool={tool} key={tool.id} />
                          ))}
                        </TabsContent>
                        <TabsContent value="ai" className="flex flex-col">
                          {hasOpenAITool && (
                            <ToolInfo tool={OpenAITool} key={OpenAITool.id} />
                          )}
                          {/* Listed by hand, like OpenAITool above, because gating it means
                              pulling it out of `simpleTools` -- and every other tool reaches
                              this menu by mapping over its group array. */}
                          {hasTextQuestionTool && (
                            <ToolInfo
                              tool={TextQuestionTool}
                              key={TextQuestionTool.id}
                            />
                          )}
                          {advancedTools.map((tool) => {
                            if ([magicAudienceType].includes(tool.id)) {
                              return null;
                            }

                            return <ToolInfo tool={tool} key={tool.id} />;
                          })}
                        </TabsContent>
                        <TabsContent value="markup" className="flex flex-col">
                          {markupTools.map((tool) => (
                            <ToolInfo tool={tool} key={tool.id} />
                          ))}
                          <GrazeMenuItem
                            icon={<CursorTextIcon />}
                            label="Text"
                            onClick={() => {
                              tools["text"]?.onSelect("actions-menu");
                              closeToolbar();
                            }}
                          />
                          <GrazeMenuItem
                            icon={<ArrowRightIcon />}
                            label="Arrow"
                            onClick={() => {
                              tools["arrow"]?.onSelect("actions-menu");
                              closeToolbar();
                            }}
                          />
                          <GrazeMenuItem
                            icon={<StickyNoteIcon className="w-4 h-4" />}
                            label="Sticky Note"
                            shortcut="n"
                            onClick={() => {
                              tools["note"]?.onSelect("actions-menu");
                              closeToolbar();
                            }}
                          />
                        </TabsContent>
                        <TabsContent value="custom" className="flex flex-col">
                          <CustomNodeTab />
                        </TabsContent>
                      </Tabs>
                    </PopoverContent>
                  </Popover>
                  {orderingTools.length > 0 && (
                    <Popover open={pageMenuOpen} onOpenChange={setPageMenuOpen}>
                      <SimpleTooltip message="Page layers">
                        <PopoverTrigger asChild>
                          <Button size="smIcon" variant="ghost" id="page-trigger">
                            <span className="sr-only">Page layers</span>
                            <LayersIcon className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                      </SimpleTooltip>
                      <PopoverContent
                        side="right"
                        sideOffset={16}
                        className="flex flex-col px-2 py-2 bg-slate-app"
                        id="page-list"
                        tabIndex={0}
                        onOpenAutoFocus={(e) => {
                          e.preventDefault();
                          (e.target as HTMLElement).focus();
                        }}
                      >
                        <div className="flex flex-col">
                          <SectionLabel>Ordering</SectionLabel>
                            {orderingTools.map((tool) => (
                              <ToolInfo tool={tool} key={tool.id} />
                            ))}
                            {gymTools.length > 0 && (
                              <>
                                <Separator className="my-2" />
                                <SectionLabel>Self-improvement</SectionLabel>
                                {gymTools.map((tool) => (
                                  <ToolInfo tool={tool} key={tool.id} />
                                ))}
                              </>
                            )}
                            {audienceTools.length > 0 && (
                              <>
                                <Separator className="my-2" />
                                <SectionLabel>Audience</SectionLabel>
                                {audienceTools.map((tool) => (
                                  <ToolInfo tool={tool} key={tool.id} />
                                ))}
                              </>
                            )}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </CloseToolbarContext>
              </div>
            </>
          )}
          {mode === "canvas" && (
            <>
              {(editorActions || (!isReadOnly && mode === "canvas")) && (
                <Separator className="my-2" />
              )}
              <div className="flex flex-col gap-1">
                <SimpleTooltip message="Select Tool">
                  <Button
                    size="smIcon"
                    variant={isSelectSelected ? "action" : "ghost"}
                    onPress={() => tools["select"]?.onSelect("actions-menu")}
                  >
                    <span className="sr-only">Select Tool</span>
                    <CursorArrowIcon />
                  </Button>
                </SimpleTooltip>
                <SimpleTooltip message="Move Tool">
                  <Button
                    size="smIcon"
                    variant={isHandSelected ? "action" : "ghost"}
                    onPress={() => tools["hand"]?.onSelect("actions-menu")}
                  >
                    <span className="sr-only">Move Tool</span>
                    <MoveIcon />
                  </Button>
                </SimpleTooltip>
              </div>
            </>
          )}

          {!isReadOnly && mode === "canvas" && (
            <>
              <Separator className="my-2" />
              <SimpleTooltip message="Eraser Tool">
                <Button
                  size="smIcon"
                  variant={isEraserSelected ? "action" : "ghost"}
                  onPress={() => tools["eraser"]?.onSelect("actions-menu")}
                >
                  <span className="sr-only">Eraser Tool</span>
                  <EraserIcon />
                </Button>
              </SimpleTooltip>
              <div className="flex-col gap-1 flex">
                <SimpleTooltip message="Undo">
                  <Button
                    variant="ghost"
                    size="smIcon"
                    onPress={() => editor.undo()}
                    id="undo-trigger"
                  >
                    <span className="sr-only">Undo</span>
                    <ResetIcon />
                  </Button>
                </SimpleTooltip>
                <SimpleTooltip message="Redo">
                  <Button
                    variant="ghost"
                    size="smIcon"
                    onPress={() => editor.redo()}
                  >
                    <span className="sr-only">Redo</span>
                    <ResetIcon className="scale-x-[-1]" />
                  </Button>
                </SimpleTooltip>
                <SimpleTooltip message="Duplicate Selected">
                  <Button
                    variant="ghost"
                    size="smIcon"
                    onPress={() =>
                      editor.duplicateShapes(editor.getSelectedShapeIds())
                    }
                  >
                    <span className="sr-only">Duplicate Selected</span>
                    <FilePlusIcon />
                  </Button>
                </SimpleTooltip>
              </div>
            </>
          )}
        </div>

        <Separator className="my-2" />
        <div className="flex flex-col gap-1">
          {!isReadOnly && (
            <SimpleTooltip message="Generate from prompt">
              <Button
                variant="ghost"
                size="smIcon"
                onPress={onStartTemplateMode}
                id="generate-feed-trigger"
                aria-label="Generate from prompt"
              >
                <MagicWandIcon />
              </Button>
            </SimpleTooltip>
          )}
          {/* Sits beside the wand because they answer the same question at
              different stages: the wand builds a feed, this one changes the
              feed that is already there. */}
          {!isReadOnly && onStartAmendMode && (
            <SimpleTooltip message="Change this feed with a prompt">
              <Button
                variant="ghost"
                size="smIcon"
                onPress={onStartAmendMode}
                id="amend-feed-trigger"
                aria-label="Change this feed with a prompt"
              >
                <Pencil2Icon />
              </Button>
            </SimpleTooltip>
          )}
          <button
            type="button"
            id="start-tour"
            className="opacity-0 fixed top-0 left-0 -translate-x-full -translate-y-full hidden"
            style={{
              visibility: "hidden",
            }}
            onClick={() => setRunTour(true)}
          >
            Start Tour
          </button>
          <HelpMenu
            startTour={() => setRunTour(true)}
            tools={customTools}
            trigger={
              <Button
                variant="ghost"
                size="smIcon"
                className="pointer-events-auto"
                {...preventBubble}
              >
                <span className="sr-only">Help documentation</span>
                <QuestionMarkIcon />
              </Button>
            }
          />
        </div>
      </div>
    </div>
  );
}

function CollapseMenuItem() {
  const editor = useEditor();

  return (
    <TldrawUiMenuItem
      label="Toggle Collapse"
      id="toggle-collapse"
      onSelect={() => {
        const shapeId = editor.getSelectedShapeIds()[0];
        if (!shapeId) return;
        const shape = editor.getShape<BindableShape>(shapeId);

        if (!shape) return;
        if (shape.type === feedType) return;

        editor.updateShape({
          id: shape.id,
          type: shape.type,
          props: {
            ...shape.props,
            isCollapsed: !shape.props.isCollapsed,
          },
        });
      }}
    />
  );
}

const ViewJsonContext = createContext<(() => void) | undefined>(undefined);

function ContextMenu(props: TLUiContextMenuProps) {
  const editor = useEditor();
  const { toast } = useToast();
  const isReadOnly = use(ReadonlyEditorContext);
  const [selectedShapes, setSelectedShapes] = useState<TLShapeId[]>([]);
  const [addTitleDialogOpen, setAddTitleDialogOpen] = useState(false);
  const [addDescriptionDialogOpen, setAddDescriptionDialogOpen] =
    useState(false);
  const [isReadOnlyShape, setIsReadOnlyShape] = useState(false);

  useEffect(() => {
    function onChange() {
      const shape = editor.getSelectedShapes()[0];

      if (!shape) return;

      setIsReadOnlyShape((shape as BindableShape).props.isReadOnly ?? false);
    }

    editor.on("change", onChange);

    return () => {
      editor.off("change", onChange);
    };
  }, [editor]);

  return (
    <>
      <DefaultContextMenu {...props}>
        <TldrawUiMenuGroup id="modify">
          <ArrangeMenuSubmenu />
          <ReorderMenuSubmenu />

          <TldrawUiMenuItem
            label="Export to PNG"
            id="export-to-png"
            onSelect={async () => {
              const shapeIds = editor.getCurrentPageShapeIds();

              if (shapeIds.size === 0) return alert("No shapes on the canvas");

              async function exportImage() {
                return editor.toImage([...shapeIds], {
                  format: "png",
                  background: false,
                });
              }

              await exportImage();
              // Have to do it twice to get the right layout
              const { blob } = await exportImage();

              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.download = "feed.png";
              link.click();
              URL.revokeObjectURL(link.href);

              await navigator.clipboard
                .writeText(`The feed logic for "". `)
                .then(() =>
                  toast({
                    title: "Copied alt text to clipboard",
                  }),
                )
                .catch(console.error);
            }}
          />
          {!isReadOnly && (
            <>
              <TldrawUiMenuSubmenu id="theming" label="Theming">
                <TldrawUiMenuItem
                  label="Set Title"
                  id="set-title"
                  onSelect={() => {
                    setAddTitleDialogOpen(true);
                    setSelectedShapes(editor.getSelectedShapeIds());
                  }}
                />

                <TldrawUiMenuItem
                  label="Set Description"
                  id="set-description"
                  onSelect={() => {
                    setAddDescriptionDialogOpen(true);
                    setSelectedShapes(editor.getSelectedShapeIds());
                  }}
                />
                <TldrawUiMenuSubmenu id="color" label="Color">
                  {colorVariants.map((color) => (
                    <RadixContextMenu.Item
                      key={color}
                      id={`set-color-${color}`}
                      className={cn(
                        "flex items-center gap-2 h-10 px-4",
                        "relative",
                        "after:content-[''] after:absolute after:inset-1 after:rounded-(--radius-2) after:bg-(--color-muted-2)",
                        "after:opacity-0 hover:after:opacity-100 after:transition-opacity",
                      )}
                      onSelect={() => {
                        const shapeId = editor.getSelectedShapeIds()[0];
                        if (!shapeId) return;
                        const shape = editor.getShape(shapeId);

                        if (!shape) return;

                        editor.updateShape({
                          id: shape.id,
                          type: shape.type,
                          props: {
                            ...shape.props,
                            metadata: {
                              ...(shape.props as { metadata?: NodeMetadata })
                                .metadata,
                              color,
                            },
                          },
                        });
                      }}
                    >
                      <div
                        className={cn(
                          "w-4 h-4 rounded-full",
                          theme.preview[color === "slate" ? "default" : color],
                        )}
                      />
                      {capitalCase(color)}
                    </RadixContextMenu.Item>
                  ))}
                </TldrawUiMenuSubmenu>
                <TldrawUiMenuSubmenu id="pattern" label="Pattern">
                  {patterns.map((pattern) => (
                    <RadixContextMenu.Item
                      key={pattern}
                      id={`set-color-${pattern}`}
                      className={cn(
                        "flex items-center gap-2 h-10 px-4",
                        "relative",
                        "after:content-[''] after:absolute after:inset-1 after:rounded-(--radius-2) after:bg-(--color-muted-2)",
                        "after:opacity-0 hover:after:opacity-100 after:transition-opacity",
                      )}
                      onSelect={() => {
                        const shapeId = editor.getSelectedShapeIds()[0];
                        if (!shapeId) return;
                        const shape = editor.getShape(shapeId);
                        if (!shape) return;

                        editor.updateShape({
                          id: shape.id,
                          type: shape.type,
                          props: {
                            ...shape.props,
                            metadata: {
                              ...(shape.props as { metadata?: NodeMetadata })
                                .metadata,
                              pattern,
                            },
                          },
                        });
                      }}
                    >
                      {/* <div
                        className={cn(
                          "w-4 h-4 rounded-full",
                          theme.preview[color === "slate" ? "default" : color],
                        )}
                      /> */}
                      {capitalCase(pattern)}
                    </RadixContextMenu.Item>
                  ))}
                </TldrawUiMenuSubmenu>
                <TldrawUiMenuItem
                  label="Reset"
                  id="reset"
                  onSelect={() => {
                    const shapeId = editor.getSelectedShapeIds()[0];
                    if (!shapeId) return;
                    const shape = editor.getShape(shapeId);
                    if (!shape) return;

                    editor.updateShape({
                      id: shape.id,
                      type: shape.type,
                      props: {
                        ...shape.props,
                        metadata: {
                          title: undefined,
                          description: undefined,
                          color: undefined,
                          pattern: undefined,
                        },
                      },
                    });
                  }}
                />
              </TldrawUiMenuSubmenu>
              <TldrawUiMenuItem
                label="Zoom to fit viewport"
                id="zoom-to-fit"
                onSelect={() => {
                  editor.zoomToSelection({
                    animation: { duration: 150 },
                  });
                }}
              />
              <TldrawUiMenuItem
                label="Zoom to fill viewport"
                id="zoom-to-fill"
                onSelect={() => {
                  const selection = editor.getSelectedShapeIds();
                  const bounds = Box.Common(
                    compact(
                      selection.map((id) => editor.getShapePageBounds(id)),
                    ),
                  );

                  bounds.height = Math.min(bounds.height, 300);

                  editor.zoomToBounds(bounds, {
                    animation: { duration: 150 },
                  });
                }}
              />
            </>
          )}
        </TldrawUiMenuGroup>
        <CopyMenuItem />
        {!isReadOnly && (
          <>
            <PasteMenuItem />
            <DuplicateMenuItem />
            {!isReadOnlyShape && <DeleteMenuItem />}
          </>
        )}
        <TldrawUiMenuGroup id="select-all">
          <SelectAllMenuItem />
        </TldrawUiMenuGroup>
        <TldrawUiMenuGroup id="select-all">
          <CollapseMenuItem />
        </TldrawUiMenuGroup>
      </DefaultContextMenu>
      <AlertDialog
        open={addTitleDialogOpen}
        onOpenChange={setAddTitleDialogOpen}
      >
        <AlertDialogContent asChild>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const formData = new FormData(e.target as HTMLFormElement);
              const title = formData.get("title") as string;

              if (!selectedShapes[0]) return;
              const shape = editor.getShape(selectedShapes[0]);

              if (!shape) return;

              editor.updateShape({
                id: shape.id,
                type: shape.type,
                props: {
                  ...shape.props,
                  metadata: {
                    ...(shape.props as { metadata?: NodeMetadata }).metadata,
                    title,
                  },
                },
              });
            }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Set Title</AlertDialogTitle>
              <AlertDialogDescription>
                Set a descriptive title for the node to display in the editor.
                This is useful for remembering what your logic does in the
                future.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor="title">Title</Label>
              {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
              <Input id="title" name="title" autoFocus />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction type="submit">Set Title</AlertDialogAction>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog
        open={addDescriptionDialogOpen}
        onOpenChange={setAddDescriptionDialogOpen}
      >
        <AlertDialogContent asChild>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();

              const formData = new FormData(e.target as HTMLFormElement);
              const description = formData.get("description") as string;

              if (!selectedShapes[0]) return;
              const shape = editor.getShape(selectedShapes[0]);

              if (!shape) return;

              editor.updateShape({
                id: shape.id,
                type: shape.type,
                props: {
                  ...shape.props,
                  metadata: {
                    ...(shape.props as { metadata?: NodeMetadata }).metadata,
                    description,
                  },
                },
              });
            }}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Set Description</AlertDialogTitle>
              <AlertDialogDescription>
                Set a description for the intent of your node. This will be
                shown in the info icon.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex flex-col gap-2">
              <Label htmlFor="description">Description</Label>
              {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
              <Input id="description" name="description" autoFocus />
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction type="submit">
                Set Description
              </AlertDialogAction>
            </AlertDialogFooter>
          </form>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const MyComponentInFront = track(() => {
  const inFrontOfCanvasPortalRef = use(InFrontOfCanvasPortalContext);
  const editor = useEditor();

  const [selectedShape] = useValue(
    "selected-shape",
    () => editor.getSelectedShapes(),
    [editor],
  );

  if (!selectedShape) return null;

  const bindings = editor.getBindingsToShape<LayoutBinding>(
    selectedShape,
    "layout",
  );

  if (!bindings.length) return null;

  const point = editor.pageToViewport(
    editor.getShapePageBounds(selectedShape.id)!,
  );

  return (
    <div
      ref={inFrontOfCanvasPortalRef.setEl}
      className="pointer-events-all"
      style={{
        position: "absolute",
        top: point?.y,
        left: point?.x,
        height: 64,
        transformOrigin: "center",
        transform: `translate(-40%, 50%)`,
      }}
    />
  );
});

const components: TLComponents = {
  ContextMenu: (props: TLUiContextMenuProps) => {
    return <ContextMenu {...props} />;
  },
  NavigationPanel: () => <Minimap />,
  Background: null,
  PageMenu: null,
  Toolbar: null,
  MenuPanel: null,
  StylePanel: null,
  InFrontOfTheCanvas: () => (
    <>
      <MyComponentInFront />
      <NodeCursor />
    </>
  ),
  ErrorFallback: ({ error: errorProp }) => {
    // 🔴 THIS PANEL USED TO SAY NOTHING. It read `error.rawMessage`, a field
    // tldraw has never set — `TLErrorFallbackComponent` is handed a plain
    // `Error` — so the message line rendered empty and every canvas crash,
    // however different, showed the same bare word ("TypeError") with no
    // detail, no stack, and nothing to paste into a bug report. Reported
    // 2026-09-21 on feed 35958 and undiagnosable from the screenshot alone.
    const error = errorProp instanceof Error ? errorProp : undefined;
    const name = error?.name ?? "Something went wrong";
    // A production React build minifies its own messages down to an error
    // number plus a docs link. That is still the difference between a
    // reproducible report and a shrug, so it is shown as-is.
    const message = error?.message ?? String(errorProp);
    const viewJson = use(ViewJsonContext);
    return (
      <div className="flex flex-col items-center justify-center h-full w-full">
        <div className="max-w-md flex flex-col gap-4 items-start">
          <div className="flex flex-col gap-2">
            <div className="text-2xl font-semibold">{name}</div>
            {message && (
              <div className="text-base text-slate-dim break-words">{message}</div>
            )}
          </div>
          {viewJson && (
            <Button variant="outline" onPress={viewJson}>
              View JSON
            </Button>
          )}
          {error?.stack && (
            <details className="w-full">
              <summary className="text-slate-dim cursor-pointer text-xs">
                Technical details
              </summary>
              <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs">
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  },
};

function CanvasEditorLiveFeed(props: PreviewFeedProps) {
  const editor = useEditor();
  const feed = useSuspenseQuery(feedQueryOptions(props.feedId));

  const actor = CanvasEditorMachine.useActorRef();
  const onDebugPost = (post: Post) => {
    actor.send({
      type: "START_AUDIT",
      urls: [atUriToBskyUrl(post.uri)],
      manifest: feed.data.draft_algorithm_manifest.manifest,
      editor,
      algo_id: props.feedId,
    });
  };

  return <PreviewFeed {...props} onDebugPost={onDebugPost} />;
}

export interface CanvasEditorHandle {
  setManifest: (feedVersion: FeedVersion) => void;
  /**
   * The feed as the canvas holds it RIGHT NOW, including edits the parent has
   * not been told about yet.
   *
   * An amendment has to be computed against what the author is looking at. The
   * `feedVersion` prop is the last SAVED version, so amending from it would
   * silently discard everything drawn since the page loaded and hand it back as
   * a proposal to accept.
   *
   * It returns the order and sort settings alongside the manifest for the same
   * reason: they are the other half of what the canvas is holding, and restoring
   * a discarded amendment from the saved values would drop unsaved changes to
   * them.
   */
  getFeedState: () =>
    | {
        manifest: Manifest;
        order: FeedVersion["order"];
        custom_sort_settings: SortingParams | undefined;
      }
    | undefined;
}

export interface CanvasEditorProps {
  className?: string;
  feedVersion?: FeedVersion;
  imperativeHandle?: React.Ref<CanvasEditorHandle>;
  onMount?: () => void;
  onUpdate?: (output: Omit<FeedVersion, "created_at">) => void;
  editorActions?: React.ReactNode;
  topBarActions?: React.ReactNode;
  panel?: React.ReactNode;
  onStartTemplateMode?: () => void;
  onStartAmendMode?: () => void;
  onViewJson?: () => void;
  hideUi?: boolean;
  hideTools?: boolean;
  feedId?: number;
  defaultVisiblePanel?: Panel;
  ref?: React.RefObject<Editor | undefined>;
  onEdit?: () => void;
  fullscreen?: boolean;
  setFullscreen?: (fullscreen: boolean) => void;
  rootNode?: {
    title: string;
    description: string;
    variant?: "default" | "custom-node";
    custom_sort_settings?: SortingParams;
    customNodeParameters?: FeedParameter[];
    customNodeParameterGroups?: {
      name: string;
      id: string;
      description?: string;
    }[];
  };
  mode?: "canvas" | "json";
}

function getTopMostShape(editor: Editor, e: React.PointerEvent) {
  const point = editor.screenToPage({
    x: e.clientX,
    y: e.clientY,
  });
  const shapes = editor?.getShapesAtPoint(point) || [];
  const parentIds = shapes.map((s) => s.parentId);
  let topMostShape;

  for (const shape of shapes) {
    if (parentIds.includes(shape.id)) continue;
    topMostShape = shape;
    break;
  }

  return topMostShape;
}

const CanvasEditorImpl = function CanvasEditorImpl({
  className,
  ref,
  feedVersion,
  onUpdate,
  imperativeHandle,
  editorActions,
  onViewJson,
  topBarActions,
  feedId: grazeFeedId,
  onStartTemplateMode,
  onStartAmendMode,
  hideUi,
  hideTools,
  onEdit,
  fullscreen,
  setFullscreen,
  rootNode,
  onMount: onMountProp,
  mode = "canvas",
}: CanvasEditorProps) {
  const isCustomNodeEditor = use(CustomNodeEditorContext);
  const innerEditor = useRef<Editor>(undefined);
  const [readonlyEditorVisible, setReadonlyEditorVisible] = useState(false);
  const [isUsingMoveTool, setIsUsingMoveTool] = useState(false);
  // While one of our placing tools is active, the blocks' HTML must not eat
  // the click: a layer header, a group's blank body and the Page box's own
  // padding all swallowed pointer-down, so the tool only worked on empty
  // canvas. Mirrors the hand tool's class.
  const [isPlacingBlock, setIsPlacingBlock] = useState(false);
  const isMobile = useIsMobile();
  const isReadOnly = !onUpdate;
  // 🔑 NO FLAG IS READ HERE ANY MORE. The gym was the last flag-gated tool in this palette; with
  // it generally available, every tool the canvas offers is offered to everyone.
  // Natural Language and Ask a Question are GENERALLY AVAILABLE (Devin, 2026-09-22:
  // "roll out text predicate and the new jev node to general availability now").
  // Until then each followed a beta flag of its own -- `text_predicate` (with
  // `openai_prompt` as a legacy synonym, since the operator was renamed and the flag
  // was not) and `text_question`.
  //
  // The flags are left in `users.feature_flags` deliberately rather than swept: they
  // cost nothing, and they are the record of who was in the beta. Nothing reads them
  // for these two nodes any more.
  //
  // ⚠️ GA removes the AUDIENCE gate, not the budget. Both nodes stay metered -- 2,000
  // posts an hour for Natural Language, 500 questions an hour for Ask a Question -- and
  // that per-feed cap is now the only thing standing between one busy feed and the
  // single GPU pod that serves every feed. See RUNBOOK-rate-limits-rollout-2026-09.md.
  const showOpenAITool = true;
  const showTextQuestionTool = true;
  // Audiences are GENERALLY AVAILABLE (Devin, 2026-09-21: "release lenses and
  // filters / layers etc out to public release"). Until then they followed the
  // `lenses` beta flag (with `personalization` as a legacy synonym), and Audience
  // Groups followed a second flag, `lens_groups`, held back until feeder-rs and
  // feed-processor both read the v3 contract — they have since 2026-09-13, and the
  // live per-lane verification of 2026-09-16 exercised groups end to end. Both
  // flags are inert now; the names stay in `FeatureFlag` so rows that hold them
  // keep validating. The serve side never gated anyone: feeder-rs applies a
  // feed's lens to every reader, so what a creator draws here has always served.
  const showAudienceTools = true;
  const showGroupTool = true;
  // Who may put a feed in the gym.
  //
  // 🔴 THIS HAS BEEN WRONG IN BOTH DIRECTIONS IN ONE DAY, which is why it is spelled out. v2 asked
  // `/api/v1/feed_gym/availability`, an endpoint gated on a server-side allowlist. Deleting v2's
  // router took that endpoint away; the auth middleware answers 401 BEFORE routing, so the query
  // failed CLOSED and the tool silently vanished for every creator. The fix removed the gate
  // entirely — which swung it the other way, to all 8,632 feeds, as a side effect of a teardown
  // rather than a decision.
  //
  // It was a FLAG — the beta cohort — so the radius was a grant in the database rather than a
  // deploy, and widening it to general availability was a decision someone could take and take
  // back. 🔴 THAT DECISION IS TAKEN: 2026-10-02, generally available. 23 accounts held the flag;
  // every creator now sees the tool.
  //
  // Opening the TOOL is not opening the experiment. Nothing enrols a feed but its owner adding
  // the layer, the share is capped where it always was, and a gym with nothing running serves the
  // feed's own ordering — so the cost of a creator trying it and leaving it idle is zero. Those
  // are the three properties that make this safe to open, and all three predate this line.
  //
  // ⚠️ This gated the PALETTE, not the canvas. `registeredTools` below always registers the gym
  // tool, so a creator already carrying a layer kept seeing and editing it whatever this said —
  // losing the flag never stranded a feed that was already taking part.
  const showGymTool = true;
  const toolOptions = useMemo<ToolOptions>(
    () => ({
      showOpenAITool,
      showTextQuestionTool,
      showLayerTools: !isCustomNodeEditor,
      showAudienceTools,
      showGroupTool,
      showGymTool,
    }),
    [
      showOpenAITool,
      showTextQuestionTool,
      isCustomNodeEditor,
      showAudienceTools,
      showGroupTool,
      showGymTool,
    ],
  );
  const customTools = useMemo(() => getCustomTools(toolOptions), [toolOptions]);
  // 🔴 The tools REGISTERED with tldraw must not change while the canvas is
  // mounted. tldraw rebuilds its Editor whenever the tools array changes, and
  // a rebuilt Editor has none of the handlers `onMount` attached — the canvas
  // keeps drawing, but nothing the creator does reaches the form, and Save
  // writes the stale copy. Gym availability arrives asynchronously, so the gym
  // tool is always registered here; `toolOptions` still decides whether the
  // palette SHOWS it. Everything else in the options is a feature flag, which
  // is loaded before this component renders and does not move.
  const registeredTools = useMemo(
    () => getCustomTools({ ...toolOptions, showGymTool: true }),
    [toolOptions],
  );

  useLayoutEffect(() => {
    if (!isReadOnly) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement) {
        return;
      }

      if (["v", "d"].includes(e.key) && e.metaKey) {
        e.preventDefault();
        e.stopPropagation();
      }
    }

    window.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [isReadOnly]);

  const visiblePanel = CanvasEditorMachine.useSelector(
    (s) => s.context.visiblePanel,
  );
  // tldraw renders its container as `tl-theme__light` and ADDS the dark class
  // once, in an effect. Any later React re-render of that element re-applies
  // the className and the dark class is gone — on a dark-mode OS the blocks
  // kept their dark backgrounds while the text fell back to the light theme's
  // near-black, and the "Add a layer" button read black-on-black the moment
  // the palette was used. Re-assert the class after every commit.
  useLayoutEffect(() => {
    const editor = innerEditor.current;
    if (!editor) return;
    const container = editor.getContainer();
    const dark = editor.user.getIsDarkMode();
    container.classList.toggle("tl-theme__dark", dark);
    container.classList.toggle("tl-theme__light", !dark);
  });

  const onMountJson = useEvent((editor: Editor) => {
    innerEditor.current = editor;
  });
  // One initialisation per Editor INSTANCE, not per component. tldraw
  // re-invokes `onMount` on the same editor when the prop changes (the
  // canvas/JSON switch), which must stay a no-op — but it also rebuilds the
  // Editor when its tools or shape utils change, and a fresh instance that
  // skipped this would have no change handler, no ref, and no side effects.
  // A component-level boolean was exactly that trap.
  const mountedEditors = useRef(new WeakSet<Editor>());
  const onMount = useEvent((editor: Editor) => {
    if (mountedEditors.current.has(editor)) return;
    mountedEditors.current.add(editor);
    setUserPreferences({ colorScheme: "system", id: "user" });

    innerEditor.current = editor;
    if (ref) {
      if (typeof ref === "function") {
        (ref as (editor: Editor) => void)(editor);
      } else {
        ref.current = editor;
      }
    }

    const snapshot = feedVersion ? getSnapshot(feedVersion) : undefined;

    const rootShape =
      snapshot &&
      "document" in snapshot &&
      typeof snapshot.document === "object" &&
      snapshot.document &&
      "store" in snapshot.document &&
      typeof snapshot.document.store === "object" &&
      snapshot.document.store &&
      ((snapshot.document.store as Record<string, unknown>)["shape:feed"] as
        | FeedShape
        | CustomNodeRootShape
        | undefined);

    const cantMigrateCustomNodeRoot =
      rootNode?.variant === "custom-node" &&
      rootShape &&
      isFeedShape(rootShape);

    if (rootShape && !cantMigrateCustomNodeRoot) {
      if (rootShape && isFeedShape(rootShape) && !rootShape.props.order) {
        rootShape.props.order = feedVersion?.order || "new";
      }

      editor.loadSnapshot(snapshot);
      // A snapshot saved before layers were blocks has none, and one whose
      // layers disagree with what the feed has stored is the known
      // snapshot-vs-manifest drift. Either way the stored settings win. Done
      // before the change handler below is registered, so it dirties nothing.
      const feed = getFeedShape(editor);
      if (feed) reconcileLayerShapes(editor, feed, feedVersion);
    } else {
      buildInitialEditor(editor, feedVersion, rootNode);
    }

    editor.deleteShape(previewId);

    // Update parent bindings when child binding change
    editor.sideEffects.registerAfterChangeHandler("shape", (shape) => {
      const parentShapes = editor.getBindingsToShape<LayoutBinding>(
        shape,
        "layout",
      );

      for (const parentShape of parentShapes) {
        const util = editor.getBindingUtil(parentShape);
        util.onAfterCreate?.({
          binding: parentShape,
          updateArrows: false,
        } as BindingOnCreateOptions<LayoutBinding>);
      }
    });

    clearAuditLog(innerEditor.current);

    // Disabled any fancy url embedding
    editor.registerExternalContentHandler("url", (content) => {
      const center = content.point ?? editor.getViewportPageBounds().center;

      editor.createShape({
        type: "text",
        x: center.x,
        y: center.y,
        props: {
          richText: toRichText(content.url),
        },
      });
    });

    if (!onUpdate) {
      const safeToDeleteShapes = new Set<TLShapeId>();

      editor.sideEffects.registerBeforeDeleteHandler("shape", (s) => {
        if (s.id.includes("debug-annotation-")) {
          return;
        }

        if (safeToDeleteShapes.has(s.id)) {
          return;
        }

        return false;
      });

      editor.sideEffects.registerBeforeDeleteHandler("binding", (s) => {
        const fromShape = editor.getShape(s.fromId);

        if (fromShape && fromShape.type === customNodeType) {
          const children = allBoundChildren(editor, fromShape);

          for (const child of children) {
            safeToDeleteShapes.add(child.id);
          }

          return;
        }

        return false;
      });

      // rAF doesn't work :(
      setTimeout(() => {
        centerContent(editor, {
          accountForSidebar:
            hideUi === true || !visiblePanel ? false : !isMobile,
        });
        // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
        setReadonlyEditorVisible(true);
      }, 0);

      requestAnimationFrame(() => onMountProp?.());

      return;
    }

    editor.on("update", () => {
      const toolId = editor.getCurrentTool().id;
      if (isMobile) {
        // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
        setIsUsingMoveTool(toolId === "hand");
      }
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
      setIsPlacingBlock(registeredTools.some((t) => t.id === toolId));
    });

    // If a shape is created on a bind target, create a binding
    editor.sideEffects.registerAfterCreateHandler("shape", (shape) => {
      if (!isBindableShape(shape)) {
        return;
      }

      const activeTool = editor.getCurrentToolId();
      const isCustomTool = registeredTools.some((t) => t.id === activeTool);

      // Only do this for our custom tools
      if (!isCustomTool) {
        return;
      }
      // Layer tools place their own shape into the Feed's Page section
      // (addLayerShape), wherever the pointer was. Binding here as well made
      // two bindings to one layer: a gap at the top of the stack and a lane
      // counted twice.
      if (orderingLayerToolIds.has(activeTool) || activeTool === GymLayerTool.id) {
        return;
      }

      const pageAnchor = editor
        .getShapePageTransform(shape)
        .applyToPoint({ x: 0, y: 0 });

      const util = editor.getShapeUtil<BindableUtil>(shape);
      const target = util.getTargetContainer(shape, pageAnchor);

      // Only do root shapes, not bound shapes
      if (shape.parentId !== "page:page") {
        return;
      }

      if (target) {
        const targetUtil = editor.getShapeUtil(target) as BindableUtil<
          FeedShape | LogicShape
        >;

        if (targetUtil && !targetUtil.canPlaceInShape(target, shape)) {
          return;
        }

        const { index } = util.getBindingIndexForPosition(
          shape,
          target,
          pageAnchor,
        );

        editor.createBinding<LayoutBinding>({
          id: createBindingId(),
          type: "layout",
          fromId: target.id,
          toId: shape.id,
          props: {
            index,
            placeholder: false,
          },
        });
        editor.updateShape({
          id: shape.id,
          type: shape.type,
          parentId: target.id,
        });
      }
    });

    // A layer joining or leaving the Feed — added, deleted, dragged out or
    // back in — changes how many ways the page is divided. Refit the shares
    // once the change has landed, so three thirds do not become 75% of a page.
    // Refit the page's shares ONCE, when the operation that changed a
    // Feed→layer binding completes — not on every binding change.
    //
    // 🔴 A layer being moved is unbound and rebound inside one operation
    // (`onTranslateEnd` → `cleanUpBindings`; nudging with the arrow keys takes
    // the same path). Refitting on the delete saw the OTHER layers alone and
    // gave them the whole page; refitting on the create then fitted the moved
    // layer against that. A [0.5, 0.5] page came back as [0.66, 0.34] from a
    // single nudge — seen live 9/16. Deferring to the end of the operation
    // sees the page as it actually ends up, and it is still synchronous: the
    // handler runs before the operation returns, so nothing observes the
    // page mid-way (three thirds with one gone = 75%) and the write is folded
    // into the author's action — not a separate undo step, and not an "edit"
    // in Save's eyes.
    let pageSharesDirty = false;
    const markPageSharesDirty = (binding: TLBinding) => {
      if (binding.type !== "layout" || binding.fromId !== feedId) return;
      const to = editor.getShape(binding.toId);
      if (to && !isLayerShape(to)) return;
      pageSharesDirty = true;
    };
    editor.sideEffects.registerAfterCreateHandler("binding", markPageSharesDirty);
    editor.sideEffects.registerAfterDeleteHandler("binding", markPageSharesDirty);
    editor.sideEffects.registerOperationCompleteHandler(() => {
      if (!pageSharesDirty) return;
      // A rebuild or a drop binds layers transiently and refits once at its
      // own end (withPageRefitHeld). Stay dirty: tldraw writes the moved
      // shape back AFTER the drop finishes, and that write is the first
      // operation to complete with the page as it really ends up.
      if (pageRefitHeld()) return;
      pageSharesDirty = false;
      if (!editor.getShape(feedId)) return;
      editor.run(() => renormaliseShares(editor), { history: "ignore" });
    });

    let extraFeedShape: FeedShape;

    // We don't want the feed node to be duplicated
    editor.sideEffects.registerAfterCreateHandler("shape", (shape) => {
      if (isFeedShape(shape)) {
        extraFeedShape = shape;
        editor.deleteShape(shape);
      }

      return shape;
    });

    // Don't let te feed shape be deleted
    editor.sideEffects.registerBeforeDeleteHandler("shape", (shape) => {
      // If a feed node is duplicated we delete it
      if (extraFeedShape && shape.id === extraFeedShape.id) {
        return;
      }

      if (isFeedShape(shape)) {
        return false;
      }

      return;
    });

    // If we duplicate a bound element on the feed, delete the binding and move the element
    // The feed can only have one child
    editor.sideEffects.registerAfterCreateHandler("binding", (binding) => {
      if (!isLayoutBinding(binding)) return;

      const fromShape = editor.getShape(binding.fromId);

      if (!fromShape || !isFeedShape(fromShape)) return;

      // One RULES root; the Page section holds many layers. Decide by type.
      const created = editor.getShape(binding.toId);
      const allBindings = editor
        .getBindingsFromShape(fromShape, "layout")
        .filter((b) => {
          const s = editor.getShape(b.toId);
          return s && created
            ? isPageRegionChild(s) === isPageRegionChild(created)
            : true;
        });
      const cap = created && isPageRegionChild(created) ? 10 : 1;

      if (allBindings.length > cap) {
        editor.deleteBinding(binding);
        const toShape = editor.getShape(binding.toId);
        if (toShape && "w" in toShape.props) {
          editor.updateShape({
            id: toShape.id,
            type: toShape.type,
            x: toShape.x + toShape.props.w + 32,
          });
        }
      }
    });

    // Call onUpdate when the manifest changes
    editor.on("change", () => {
      if (!onUpdate || mode === "json") return;

      const newManifest = buildManifestFromState(editor);

      if (newManifest) {
        onUpdate({
          ...newManifest,
          metadata: {
            snapshot: editor.getSnapshot(),
          },
          content_mode: feedVersion?.content_mode,
          is_legacy: feedVersion?.is_legacy ?? false,
        });
      }
    });

    // Delete shapes bound to deleted layouts
    let lastDeletedBinding: TLShapeId | undefined;
    let lastDeletedShapeToDelete: TLShapeId[] = [];

    // Bindings are deleted first so we have to gather the toIds
    editor.sideEffects.registerBeforeDeleteHandler("binding", (binding) => {
      if (!isLayoutBinding(binding) || binding.props.placeholder) return;

      if (!lastDeletedBinding || lastDeletedBinding !== binding.fromId) {
        lastDeletedBinding = binding.fromId;
        lastDeletedShapeToDelete = [];
      }

      lastDeletedShapeToDelete.push(binding.toId);
    });

    // Then if the shape is actually deleted delete those bounds shape
    // We can just use binding cause those get created and deleted during other operations
    // (like duplicate)
    editor.sideEffects.registerBeforeDeleteHandler("shape", (shape) => {
      if (!lastDeletedBinding) return;
      if (shape.id !== lastDeletedBinding) return;

      for (const to of lastDeletedShapeToDelete) {
        const toShape = editor.getShape(to);

        if (toShape) {
          editor.deleteShape(toShape);
        }
      }
    });

    requestAnimationFrame(() => onMountProp?.());
  });

  useImperativeHandle(imperativeHandle, () => ({
    setManifest: (newFeedVersion: FeedVersion) => {
      if (!innerEditor.current) return;
      const feed = innerEditor.current.getShape<FeedShape>(feedId);
      if (!feed) return;
      rebuildEditorFromManifest(innerEditor.current, newFeedVersion);
      centerContent(innerEditor.current);
    },
    getFeedState: () => {
      if (!innerEditor.current) return undefined;
      return buildManifestFromState(innerEditor.current);
    },
  }));

  /**
   * A save made from the JSON editor cannot preserve canvas layout: the layout
   * belongs to the rules the JSON may have just replaced. What it must not do
   * is persist the PREVIOUS canvas next to the NEW manifest -- the two then
   * disagree for good, and every later reader that walks them together (Debug's
   * per-node colouring, most visibly) is aligned against the wrong nodes.
   *
   * So rebuild the canvas from the JSON first, and persist THAT snapshot, so
   * the pair always describes the same feed. Any `metadata` carried in the JSON
   * text itself is discarded rather than trusted -- pasting a feed's JSON back
   * with its own `metadata` is exactly how a stale snapshot gets re-persisted
   * against a manifest that has moved on. When there is no canvas to rebuild,
   * persist no snapshot at all: a missing one makes the canvas rebuild from the
   * manifest at load, which is consistent by construction.
   *
   * The rebuild runs on every keystroke that parses, which is well before the
   * text describes a whole feed, so it has to be allowed to fail: a rebuild that
   * throws leaves the canvas as it was (see `rebuildEditorFromManifest`) and the
   * failure is reported like any other validation error, keeping the JSON the
   * user is still typing. Nothing is saved from an edit the canvas cannot draw.
   */
  const onJsonEditorChange = useEvent((updates: FeedVersion) => {
    const editor = innerEditor.current;

    if (!editor) {
      onUpdate?.({ ...updates, metadata: undefined });
      return;
    }

    try {
      rebuildEditorFromManifest(editor, updates, rootNode);
    } catch (error) {
      throw new Error(
        `This manifest cannot be drawn on the canvas: ${
          (error as Error).message
        }`,
        { cause: error },
      );
    }

    onUpdate?.({
      ...updates,
      metadata: buildManifestFromState(editor)
        ? { snapshot: editor.getSnapshot() }
        : undefined,
    });
  });

  const hasActiveFeed = Boolean(feedVersion?.created_at);
  const machine = CanvasEditorMachine.useActorRef();

  useEffect(() => {
    if (isReadOnly) {
      isReadOnlyEditorPage.set(true);
    }

    return () => {
      if (isReadOnly) {
        isReadOnlyEditorPage.set(false);
      }
    };
  }, [isReadOnly]);

  const onMountMobile = useEvent(() => {
    if (onUpdate) {
      machine.send({ type: "SET_VISIBLE_PANEL", panel: undefined });
    } else {
      machine.send({ type: "SET_VISIBLE_PANEL", panel: "feed" });
    }
  });

  useEffect(() => {
    if (isMobile && hasActiveFeed) {
      onMountMobile();
    }
  }, [machine, isMobile, hasActiveFeed, onMountMobile]);

  const [mouseInsideEditor, setMouseInsideEditor] = useState(false);
  // Bumped only to recover from a crashed canvas — see `viewJsonAndRecover`.
  // Unused on every normal path, so `<Tldraw>` keeps its identity and toggling
  // canvas/JSON the ordinary way still does not remount the editor.
  const [canvasGeneration, setCanvasGeneration] = useState(0);

  useEffect(() => {
    if (mouseInsideEditor) {
      document.body.style.overscrollBehaviorX = "none";
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overscrollBehaviorX = "";
      document.body.style.overflow = "";
    };
  }, [mouseInsideEditor]);

  useEffect(() => {
    if (mode === "canvas" && innerEditor.current) {
      onMount(innerEditor.current);
    }
  }, [mode, onMount]);

  const ExportProvider = useMemo(() => {
    return ({ children }: { children: React.ReactNode }) => {
      return (
        // eslint-disable-next-line @eslint-react/no-context-provider
        <CanvasEditorMachine.Provider logic={canvasEditorMachine.provide({})}>
          <ReadonlyEditorContext value={isReadOnly}>
            <TldrawUiContextProvider>
              <QueryClientProvider client={queryClient}>
                <TooltipProvider>{children}</TooltipProvider>
              </QueryClientProvider>
            </TldrawUiContextProvider>
          </ReadonlyEditorContext>
        </CanvasEditorMachine.Provider>
      );
    };
  }, [isReadOnly]);

  // 🔴 "View JSON" IS RENDERED INSIDE THE THING THAT CRASHED. tldraw's
  // ErrorFallback replaces everything inside `<Tldraw>`, and the JSON panel
  // (`mode === "json"`) is one of that subtree's children — so flipping the
  // mode alone re-renders a boundary that is still latched in its error state
  // and nothing appears. The button looked dead precisely when it was the only
  // way out, which is how it was reported on 2026-09-21: "view json does
  // nothing".
  //
  // A React error boundary only leaves its error state when it is remounted,
  // so the escape hatch has to be a new `key`. Bumping it here and nowhere else
  // keeps the normal canvas/JSON toggle a plain prop change.
  // Stays `undefined` when the host has no JSON view to switch to — the
  // custom-node editor, for one. The fallback keys the button off this, so
  // wrapping unconditionally would put a genuinely dead button on screen.
  const viewJsonAndRecover = useMemo(
    () =>
      onViewJson
        ? () => {
            onViewJson();
            setCanvasGeneration((generation) => generation + 1);
          }
        : undefined,
    [onViewJson],
  );

  return (
    <ReadonlyEditorContext value={isReadOnly}>
      {/* Which saved feed this canvas is editing, for the shapes. `grazeFeedId` already reaches the
          side panels as a prop, but a shape could not get at it -- and the feed shape needs it for
          the gym controls, which write `algorithms.settings` rather than the manifest. */}
      <GrazeFeedIdContext value={grazeFeedId}>
      <ViewJsonContext value={viewJsonAndRecover}>
        <div
          onMouseEnter={() => setMouseInsideEditor(true)}
          onMouseLeave={() => setMouseInsideEditor(false)}
          className={cn(
            "h-full w-full canvas-editor relative flex flex-col",
            isReadOnly && !readonlyEditorVisible && "hide-canvas",
            visiblePanel && "panel-open",
            hideUi && "hide-ui",
            isMobile && "mobile-canvas",
            className,
          )}
          onScroll={(e) => e.stopPropagation()}
          // This makes it so that when a user clicks on a shape, it gets selected
          // Otherwise if a parent is selected that one will be translated.
          onPointerDownCapture={(e) => {
            if (!innerEditor.current) return;
            if (
              e.target instanceof HTMLElement &&
              e.target.closest("[data-radix-popper-content-wrapper]")
            ) {
              return;
            }

            const topMostShape = getTopMostShape(innerEditor.current, e);

            if (topMostShape) {
              if (innerEditor.current.getCurrentToolId() === "eraser") {
                e.stopPropagation();
              } else {
                innerEditor.current.select(topMostShape.id);
              }
            }
          }}
          onPointerUpCapture={(e) => {
            if (!innerEditor.current) return;

            const topMostShape = getTopMostShape(innerEditor.current, e);

            if (
              innerEditor.current.getCurrentToolId() === "eraser" &&
              topMostShape
            ) {
              innerEditor.current.deleteShape(topMostShape);
              e.stopPropagation();
            }
          }}
        >
          <Tldraw
            key={canvasGeneration}
            onUiEvent={(e) => {
              setTimeout(() => {
                if (e !== "paste") return;
                if (!innerEditor.current) return;
                clearAuditLog(innerEditor.current);
              }, 10);
            }}
            onMount={mode === "canvas" ? onMount : onMountJson}
            shapeUtils={shapes}
            bindingUtils={bindingUtils}
            components={
              isReadOnly
                ? {
                    ...components,
                    SelectionForeground: null,
                    QuickActions: null,
                    Canvas: mode === "canvas" ? DefaultCanvas : null,
                    NavigationPanel:
                      mode === "canvas" ? components.NavigationPanel : null,
                  }
                : {
                    ...components,
                    Canvas: mode === "canvas" ? DefaultCanvas : null,
                    NavigationPanel:
                      mode === "canvas" ? components.NavigationPanel : null,
                  }
            }
            tools={
              isCustomNodeEditor
                ? [...registeredTools, ParamCompareTool, EachTool]
                : registeredTools
            }
            options={{
              createTextOnCanvasDoubleClick: false,
              maxPages: 1,
              exportProvider: ExportProvider,
            }}
            overrides={getUiOverrides({
              isReadOnly,
              isCustomNode: isCustomNodeEditor,
              toolOptions,
            })}
            className={cn(
              "bg-slate-subtle flex-1",
              isUsingMoveTool && "move-tool-active",
              isPlacingBlock && "placing-block",
            )}
          >
            {!hideUi &&
              (isMobile ? (
                <div className="absolute inset-0 flex pointer-events-none">
                  <div className="flex flex-col flex-1 w-full">
                    {!hideTools && (
                      <div className="pointer-events-auto flex items-center gap-0.5 p-2 self-stretch bg-slate-app border-b border-slate-6 dark:border-slatedark-6">
                        <div className="hidden min-[475px]:flex items-center gap-0.5 flex-1 min-w-0 overflow-auto">
                          <Button
                            size="xs"
                            variant={
                              visiblePanel === undefined ? "action" : "ghost"
                            }
                            onPress={() =>
                              machine.send({
                                type: "SET_VISIBLE_PANEL",
                                panel: undefined,
                              })
                            }
                          >
                            Canvas
                          </Button>
                          {hasActiveFeed && (
                            <Button
                              size="xs"
                              variant={
                                visiblePanel === "feed" ? "action" : "ghost"
                              }
                              onPress={() =>
                                machine.send({
                                  type: "SET_VISIBLE_PANEL",
                                  panel:
                                    visiblePanel === "feed"
                                      ? undefined
                                      : "feed",
                                })
                              }
                            >
                              <span className="hidden md:block">Live</span> Feed
                            </Button>
                          )}
                          {onUpdate && (
                            <SimpleTooltip message="This feature is currently down for maintenance">
                              <div>
                                <Button
                                  size="xs"
                                  disabled
                                  variant={
                                    visiblePanel === "generate-feed"
                                      ? "action"
                                      : "ghost"
                                  }
                                  onPress={() =>
                                    machine.send({
                                      type: "SET_VISIBLE_PANEL",
                                      panel:
                                        visiblePanel === "generate-feed"
                                          ? undefined
                                          : "generate-feed",
                                    })
                                  }
                                  id="generate-feed-tool"
                                >
                                  Preview
                                </Button>
                              </div>
                            </SimpleTooltip>
                          )}
                          <Button
                            size="xs"
                            variant={
                              visiblePanel === "post" ? "action" : "ghost"
                            }
                            onPress={() =>
                              machine.send({
                                type: "SET_VISIBLE_PANEL",
                                panel:
                                  visiblePanel === "post" ? undefined : "post",
                              })
                            }
                            id="debug-post-tool"
                          >
                            Debug
                          </Button>
                          {onEdit && (
                            <Button size="xs" variant="ghost" onPress={onEdit}>
                              Edit
                            </Button>
                          )}
                          {hasActiveFeed && (
                            <Button
                              size="xs"
                              variant={
                                visiblePanel === "add-to-feed"
                                  ? "action"
                                  : "ghost"
                              }
                              onPress={() =>
                                machine.send({
                                  type: "SET_VISIBLE_PANEL",
                                  panel:
                                    visiblePanel === "add-to-feed"
                                      ? undefined
                                      : "add-to-feed",
                                })
                              }
                            >
                              Insert posts
                            </Button>
                          )}
                        </div>
                        <div className="min-[475px]:hidden flex-1 min-w-0 flex items-center gap-0.5">
                          {onEdit && (
                            <Button
                              size="smIcon"
                              variant="outline"
                              onPress={onEdit}
                            >
                              <span className="sr-only">Edit</span>
                              <PencilIcon />
                            </Button>
                          )}

                          <Select
                            value={visiblePanel || "canvas"}
                            onValueChange={(newValue) => {
                              if (!newValue) return;
                              machine.send({
                                type: "SET_VISIBLE_PANEL",
                                panel:
                                  newValue === "canvas"
                                    ? undefined
                                    : (newValue as Panel),
                              });
                            }}
                          >
                            <SelectTrigger size="sm">
                              <SelectValue placeholder="Select a panel" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="canvas">Canvas</SelectItem>
                              {hasActiveFeed && (
                                <SelectItem value="feed">Live Feed</SelectItem>
                              )}
                              {onUpdate && (
                                <>
                                  <SelectItem value="generate-feed">
                                    Preview
                                  </SelectItem>
                                  <SelectItem value="custom-node">
                                    Custom Node
                                  </SelectItem>
                                </>
                              )}
                              <SelectItem value="post">Debug</SelectItem>
                              {hasActiveFeed && (
                                <SelectItem value="add-to-feed">
                                  Insert posts
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          {topBarActions}
                        </div>
                      </div>
                    )}
                    <div className="flex relative flex-1 z-50">
                      {visiblePanel ? (
                        <div className="bg-slate-app absolute inset-0 pointer-events-auto md:rounded-lg overflow-auto">
                          {visiblePanel === "post" && feedVersion && (
                            <WorkbenchDebugPost
                              feedId={grazeFeedId}
                              feedVersion={feedVersion}
                            />
                          )}
                          {visiblePanel === "feed" &&
                            feedVersion &&
                            grazeFeedId && (
                              <CanvasEditorLiveFeed
                                feedId={grazeFeedId}
                                feedVersion={feedVersion}
                                workbench={false}
                              />
                            )}
                          {visiblePanel === "generate-feed" && feedVersion && (
                            <WorkbenchPreviewFeed
                              feedId={grazeFeedId}
                              feedVersion={feedVersion}
                            />
                          )}
                          {visiblePanel === "add-to-feed" && grazeFeedId && (
                            <AddPostPanel
                              order={feedVersion?.order}
                              feedId={grazeFeedId}
                            />
                          )}
                        </div>
                      ) : (
                        <>
                          <Sidebar
                            editorActions={editorActions}
                            onStartTemplateMode={onStartTemplateMode}
                            onStartAmendMode={onStartAmendMode}
                            customTools={customTools}
                            mode={mode}
                          />
                          {mode === "json" && (
                            <div className="pointer-events-auto absolute inset-0 left-[50px]">
                              <LazyJsonEditor
                                feedVersion={
                                  {
                                    ...defaultFeedVersion,
                                    order: feedVersion?.order,
                                    manifest: feedVersion?.manifest,
                                    custom_sort_settings:
                                      feedVersion?.custom_sort_settings,
                                    content_mode: feedVersion?.content_mode,
                                  } as unknown as FeedVersion
                                }
                                onChange={
                                  onUpdate ? onJsonEditorChange : undefined
                                }
                              />
                            </div>
                          )}
                        </>
                      )}
                      {onUpdate &&
                        fullscreen === false &&
                        visiblePanel === undefined &&
                        setFullscreen && (
                          <div className="absolute inset-0 bg-slate-app bg-opacity-80 z-100 flex items-center justify-center pointer-events-auto">
                            <Button
                              variant="outline"
                              onPress={() => setFullscreen(true)}
                            >
                              Start Editing
                            </Button>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 flex pointer-events-none">
                  <Sidebar
                    customTools={customTools}
                    editorActions={editorActions}
                    onStartTemplateMode={onStartTemplateMode}
                    onStartAmendMode={onStartAmendMode}
                    mode={mode}
                  />
                  {mode === "json" && (
                    <div className="pointer-events-auto absolute inset-0 left-[50px]">
                      <LazyJsonEditor
                        feedVersion={
                          {
                            ...defaultFeedVersion,
                            order: feedVersion?.order,
                            manifest: feedVersion?.manifest,
                            custom_sort_settings:
                              feedVersion?.custom_sort_settings,
                            content_mode: feedVersion?.content_mode,
                          } as unknown as FeedVersion
                        }
                        onChange={onUpdate ? onJsonEditorChange : undefined}
                      />
                    </div>
                  )}
                  <div className="flex flex-col items-end flex-1 min-w-0 relative">
                    {!hideTools && (
                      <div className="flex items-center p-2 self-stretch">
                        <div className="flex-1" />
                        <div className="pointer-events-auto flex items-center gap-2">
                          {hasActiveFeed && (
                            <SimpleTooltip message="View live feed">
                              <Toggle
                                size="sm"
                                pressed={visiblePanel === "feed"}
                                onPressedChange={() =>
                                  machine.send({
                                    type: "SET_VISIBLE_PANEL",
                                    panel:
                                      visiblePanel === "feed"
                                        ? undefined
                                        : "feed",
                                  })
                                }
                              >
                                <span className="sr-only">View live feed</span>
                                <ListCheck />
                              </Toggle>
                            </SimpleTooltip>
                          )}
                          {onUpdate && (
                            <SimpleTooltip message="This feature is currently down for maintenance">
                              <div>
                                <Toggle
                                  size="sm"
                                  disabled
                                  pressed={visiblePanel === "generate-feed"}
                                  onPressedChange={() =>
                                    machine.send({
                                      type: "SET_VISIBLE_PANEL",
                                      panel:
                                        visiblePanel === "generate-feed"
                                          ? undefined
                                          : "generate-feed",
                                    })
                                  }
                                  id="generate-feed-tool"
                                >
                                  <span className="sr-only">
                                    Generate preview feed
                                  </span>
                                  <ListPlus />
                                </Toggle>
                              </div>
                            </SimpleTooltip>
                          )}
                          {hasActiveFeed && (
                            <SimpleTooltip message="Add/remove posts from feed">
                              <Toggle
                                size="sm"
                                pressed={visiblePanel === "add-to-feed"}
                                onPressedChange={() =>
                                  machine.send({
                                    type: "SET_VISIBLE_PANEL",
                                    panel:
                                      visiblePanel === "add-to-feed"
                                        ? undefined
                                        : "add-to-feed",
                                  })
                                }
                              >
                                <span className="sr-only">
                                  Add/remove posts from feed
                                </span>
                                <SquarePlusIcon />
                              </Toggle>
                            </SimpleTooltip>
                          )}
                          <SimpleTooltip message="Debug post">
                            <Toggle
                              size="sm"
                              pressed={visiblePanel === "post"}
                              onPressedChange={() =>
                                machine.send({
                                  type: "SET_VISIBLE_PANEL",
                                  panel:
                                    visiblePanel === "post"
                                      ? undefined
                                      : "post",
                                })
                              }
                              id="debug-post-tool"
                            >
                              <span className="sr-only">Debug post</span>
                              <BeakerIcon />
                            </Toggle>
                          </SimpleTooltip>
                          {onEdit && (
                            <SimpleTooltip message="Edit">
                              <Button
                                size="smIcon"
                                variant="ghost"
                                onPress={onEdit}
                              >
                                <span className="sr-only">Edit feed</span>
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </SimpleTooltip>
                          )}
                          {topBarActions}
                        </div>
                      </div>
                    )}
                    {visiblePanel && (
                      <div className="bg-slate-app w-[calc(100%-28px)] max-w-[520px] shadow-lg flex-1 border border-slate-7 dark:border-slatedark-7 pointer-events-auto mr-3 mb-3 mt-1 rounded-lg overflow-auto">
                        {visiblePanel === "post" && feedVersion && (
                          <WorkbenchDebugPost
                            feedId={grazeFeedId}
                            feedVersion={feedVersion}
                          />
                        )}
                        {visiblePanel === "add-to-feed" && grazeFeedId && (
                          <AddPostPanel
                            order={feedVersion?.order}
                            feedId={grazeFeedId}
                          />
                        )}
                        {visiblePanel === "feed" &&
                          feedVersion &&
                          grazeFeedId && (
                            <CanvasEditorLiveFeed
                              feedId={grazeFeedId}
                              feedVersion={feedVersion}
                              workbench={false}
                            />
                          )}
                        {visiblePanel === "generate-feed" && feedVersion && (
                          <WorkbenchPreviewFeed
                            feedId={grazeFeedId}
                            feedVersion={feedVersion}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </Tldraw>
        </div>
      </ViewJsonContext>
      </GrazeFeedIdContext>
    </ReadonlyEditorContext>
  );
};

export const CanvasEditor = function CanvasEditor({
  defaultVisiblePanel,
  testPosts,
  ...props
}: CanvasEditorProps & {
  onDebugPost?: (urls: string[]) => void;
  testPosts?: string[];
}) {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const value = useMemo(() => ({ el, setEl }), [el, setEl]);

  return (
    <InFrontOfCanvasPortalContext value={value}>
      <ColorContext value={null}>
        {/* eslint-disable-next-line @eslint-react/no-context-provider */}
        <CanvasEditorMachine.Provider
          options={{
            input: {
              visiblePanel: defaultVisiblePanel,
              debugResults: testPosts?.reduce((acc, url) => {
                acc[url] = undefined;
                return acc;
              }, {} as Record<string, boolean | undefined>),
            },
          }}
          logic={canvasEditorMachine.provide({
            actions: {
              onDebugPost: ({ event }) => {
                if (event.type !== "START_AUDIT") {
                  throw new Error("Invalid event type");
                }

                props.onDebugPost?.(event.urls);
              },
              onRemoveAuditItem: ({ event }) => {
                if (event.type !== "REMOVE_AUDIT_ITEM") {
                  throw new Error("Invalid event type");
                }

                const urls = testPosts?.filter((url) => url !== event.url);
                props.onDebugPost?.(urls || []);
              },
            },
          })}
        >
          <CanvasEditorImpl {...props} />
        </CanvasEditorMachine.Provider>
      </ColorContext>
    </InFrontOfCanvasPortalContext>
  );
};
